/**
 * /try Client Transport + Classification Helpers.
 *
 * WHAT THIS FILE DOES:
 * - Owns fetch timeout/abort plumbing shared by WebTryClient requests.
 * - Holds retry configuration constants for share creation.
 * - Normalizes transport errors into stable WebTryError codes.
 * - Builds media transcode telemetry metadata.
 *
 * WHY THIS EXISTS:
 * - Extracted verbatim from src/client/try-client.ts so the orchestrator
 *   stays under the god-module line. No behavior changes.
 *
 * CALLED BY:
 * - src/client/try-client.ts (WebTryClient)
 * - src/client/try-client-ingest.ts (ingest orchestration helpers)
 *
 * @file src/client/try-client-transport.ts
 */

import {
  TryMediaKinds,
  WebTryError
} from './try-shared.js';
import type {
  TryMediaClientTranscodeTelemetry,
  TryMediaKind,
  TryMediaTranscodeFallbackReason
} from './try-shared.js';
import { computeMediaByteAccounting, resolveMediaContentType } from './try-utils.js';

export const FetchDefaults = {
  /**
   * Default timeout for fetchWithAuth requests when the caller provides no signal.
   *
   * Prevents indefinite hangs on stalled network connections.
   * 180 seconds gives long Markdown/text ingests enough room while still
   * preventing indefinite hangs on stalled connections.
   */
  TIMEOUT_MS: 180_000
} as const;

export interface DefaultTimeoutInit {
  init: RequestInit;
  ownsSignal: boolean;
  cleanup: () => void;
}

export function withDefaultFetchTimeout(init: RequestInit, timeoutMs: number): DefaultTimeoutInit {
  if (init.signal) {
    return { init, ownsSignal: false, cleanup: () => {} };
  }

  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return {
      init: { ...init, signal: AbortSignal.timeout(timeoutMs) },
      ownsSignal: true,
      cleanup: () => {}
    };
  }

  if (typeof AbortController !== 'undefined') {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    return {
      init: { ...init, signal: controller.signal },
      ownsSignal: true,
      cleanup: () => clearTimeout(timeoutId)
    };
  }

  return { init, ownsSignal: false, cleanup: () => {} };
}

export function isFetchAbortError(error: unknown): boolean {
  const name = error && typeof error === 'object' && 'name' in error
    ? (error as { name?: unknown }).name
    : null;
  return name === 'AbortError' || name === 'TimeoutError';
}

export function createFetchAbortError(ownsSignal: boolean): Error {
  if (ownsSignal) {
    return new WebTryError('request_timeout');
  }
  if (typeof DOMException === 'function') {
    return new DOMException('The operation was aborted.', 'AbortError');
  }
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
}

export async function awaitWithFetchAbort<T>(
  start: () => Promise<T>,
  signal: AbortSignal | null | undefined,
  ownsSignal: boolean
): Promise<T> {
  if (!signal) {
    return await start();
  }
  if (signal.aborted) {
    throw createFetchAbortError(ownsSignal);
  }

  let abortHandler: (() => void) | null = null;
  try {
    return await Promise.race([
      start(),
      new Promise<never>((_resolve, reject) => {
        abortHandler = () => reject(createFetchAbortError(ownsSignal));
        signal.addEventListener('abort', abortHandler, { once: true });
      })
    ]);
  } finally {
    if (abortHandler) {
      signal.removeEventListener('abort', abortHandler);
    }
  }
}

export function normalizeFetchTransportError(error: unknown, ownsSignal: boolean): unknown {
  if (error instanceof WebTryError) {
    return error;
  }
  if (isFetchAbortError(error)) {
    return ownsSignal
      ? new WebTryError('request_timeout', { details: error })
      : error;
  }
  if (error instanceof TypeError) {
    return new WebTryError('request_network', { details: error });
  }
  return error;
}

export const ShareCreateConfig = {
  /**
   * Upper bound for a single /api/share request.
   *
   * Prevents indefinite UI hangs when network stalls.
   */
  REQUEST_TIMEOUT_MS: 6_000,
  /**
   * Maximum attempts for immediate share creation.
   *
   * Why retries are required:
   * - /api/web/ingest/media/submit returns before the background upsert path
   *   has guaranteed the content document is readable on every edge.
   * - A short `content_not_found` window is expected right after submit.
   *
   * A-011: Doubled from 6 to 12 and switched to exponential backoff so the
   * client retries long enough for the backend to converge instead of
   * surfacing "submit again" to the user.
   */
  MAX_ATTEMPTS: 12,
  /**
   * Initial backoff between retry attempts.
   *
   * Grows exponentially (500, 1000, 2000, ...) capped at MAX_RETRY_DELAY_MS.
   */
  RETRY_DELAY_MS: 500,
  /**
   * Ceiling for exponential backoff to keep total wait bounded.
   */
  MAX_RETRY_DELAY_MS: 4_000
} as const;

export const MediaUploadWsRuntime = {
  /**
   * Time to keep init-issued WS credentials in memory.
   *
   * Guards against unbounded growth when init/upload completes but submit is
   * never called (navigation, aborted flow, exceptions).
   */
  CREDENTIAL_TTL_MS: 10 * 60 * 1000,
  /**
   * Upper bound for cached credential entries in one page session.
   *
   * /try normally processes one upload at a time; this cap is a defensive
   * guardrail for unusual multi-upload flows.
   */
  MAX_CACHED_CREDENTIALS: 32,
  /** WS connect timeout. */
  CONNECT_TIMEOUT_MS: 3_000,
  /** Max wait for terminal WS event after open. */
  TERMINAL_EVENT_TIMEOUT_MS: 30_000,
  /**
   * Grace delay before falling back to polling on close.
   *
   * Some runtimes can deliver close and message callbacks with tight ordering.
   * A short grace window avoids unnecessary polling fallback when a terminal
   * message is effectively in-flight.
   */
  CLOSE_FALLBACK_GRACE_MS: 100
} as const;

/**
 * Resolves coarse media kind label from content type.
 */
export function resolveTryMediaKind(contentType: string | null): TryMediaKind {
  if (typeof contentType === 'string') {
    if (contentType.startsWith('audio/')) {
      return TryMediaKinds.AUDIO;
    }
    if (contentType.startsWith('video/')) {
      return TryMediaKinds.VIDEO;
    }
  }
  return TryMediaKinds.UNKNOWN;
}

export interface TryMediaUploadResult {
  uploadId: string;
  fileName: string;
  contentType: string;
  usedClientTranscode: boolean;
  fallbackReason: TryMediaTranscodeFallbackReason | null;
  telemetry: TryMediaClientTranscodeTelemetry;
}

/**
 * Returns true when create-share should be retried.
 */
export function shouldRetryCreateShare(error: unknown): boolean {
  if (error instanceof WebTryError) {
    if (error.message === 'content_not_found') {
      return true;
    }
    if (typeof error.status === 'number' && error.status >= 500) {
      return true;
    }
    if (error.message === 'empty_response') {
      return true;
    }
    return false;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  if (error instanceof TypeError) {
    // Browsers surface transient network failures as TypeError.
    return true;
  }

  return false;
}

/**
 * Converts low-level create-share errors into stable WebTryError codes.
 */
export function normalizeCreateShareError(error: unknown): WebTryError {
  if (error instanceof WebTryError) {
    if (error.message === 'content_not_found') {
      return new WebTryError('share_not_ready', {
        status: error.status,
        details: error.details
      });
    }
    return error;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return new WebTryError('share_create_timeout');
  }

  if (error instanceof TypeError) {
    return new WebTryError('share_create_network');
  }

  return new WebTryError('share_create_failed', { details: error });
}

/**
 * Builds media transcode telemetry metadata for submit/speculation callers.
 */
export function buildMediaTranscodeTelemetry(params: {
  originalFile: File;
  uploadedFile: File;
  usedClientTranscode: boolean;
  fallbackReason: TryMediaTranscodeFallbackReason | null;
  mediaKindHint: TryMediaKind | null;
  originalContentTypeHint: string | null;
}): TryMediaClientTranscodeTelemetry {
  const originalContentType = params.originalContentTypeHint ?? resolveMediaContentType(params.originalFile);
  const uploadedContentType = resolveMediaContentType(params.uploadedFile) ?? params.uploadedFile.type ?? null;

  const mediaKind = params.mediaKindHint ?? resolveTryMediaKind(originalContentType);
  const byteAccounting = computeMediaByteAccounting(
    params.originalFile.size,
    params.uploadedFile.size
  );

  return {
    usedClientTranscode: params.usedClientTranscode,
    fallbackReason: params.fallbackReason,
    mediaKind,
    originalContentType,
    uploadedContentType,
    originalBytes: byteAccounting.originalBytes,
    uploadedBytes: byteAccounting.uploadedBytes,
    bytesSaved: byteAccounting.bytesSaved,
    reductionRatio: byteAccounting.reductionRatio
  };
}
